/* eslint-disable jsdoc/check-param-names */
import {combineLatest, concat, MonoTypeOperatorFunction, Observable, of, OperatorFunction, switchMap, throwError} from 'rxjs';
import {catchError, filter, map, take, tap} from 'rxjs/operators';
import {HttpResponse} from '@angular/common/http';
import {NotificationService} from '../_services/gui/notification.service';

/** Filters out items in the stream that are falsy */
export function exists(): MonoTypeOperatorFunction<any> {
  return v => v.pipe(filter(v1 => !!v1));
}

/** Will perform the tap operation if the condition is true */
export function tapIf<T>(condition: (x: T) => boolean, next: (x: T) => void): MonoTypeOperatorFunction<T> {
  return val => val.pipe(
    tap(v => {
      if(condition(v)) {
        return next(v);
      }
    })
  );
}

/**
 * Converts object to an observable, combines it with the provided observable, and pipes that through a "first" filter which wants a non-null value.
 * The effect is to simply return back the provided object unless its null, in which case the object will come from the provided observable
 *
 * @param o - Object to return if it is non-null
 * @param or - Observable to get the object if the provided one is null
 * @param typeGuard - An optional type guard method to use as the filter instead of a simple null-check
 */
export function ofOr<T>(o: T, or: Observable<T>, typeGuard?: (v) => boolean): Observable<T> {
  //If a typeguard function is provided, use that to filter through the observables.  If it's not, just check if it's null
  const f = typeGuard ? typeGuard : v => !!v;
  return concat(of(o), or).pipe(filter(f), take(1));
}

/**
 * Converts an http error to an http response.  But everything else is kept the same (status code, body, headers)
 */
export function mapErrorToHttpResponse<T>(
  notificationService: NotificationService,
  statusData: {statusCode: number; i18nMessage?: string; i18nData?: Record<string, unknown>}[],
  generalData?: {i18nMessage: string; i18nData?: Record<string, unknown>}
): OperatorFunction<T, T> {
  return catchError(err => {
    let response = null;
    //Check if the response contains an error status that matches one of the provided ones
    const d = statusData.find(d1 => err.status === d1.statusCode);
    if(d) {
      console.debug(`Error status matched ${d.statusCode}.  Passing i18n message (${d.i18nMessage}) to notification service`);
      //Display any specified messages to the user
      if(d.i18nMessage) {
        notificationService.displayI18n(d.i18nMessage, d.i18nData);
      }
      //Convert the error response into a normal response with the same status, body, and headers
      response = new HttpResponse({status: err.status, body: err.error, headers: err.headers});
    } else if(generalData) {
      //This happens when there was an error, but it doesn't match a specified status, and this method was told to handle this scenario (a type of default)
      console.warn(`General error for status ${err.status}`);
      notificationService.displayI18n(generalData.i18nMessage, generalData.i18nData);
      //Convert the error response into a normal response with the same status, body, and headers
      response = new HttpResponse({status: err.status, body: err.error, headers: err.headers});
    }
    //Return the response if able, or throw the error again
    return response && of(response) || throwError(err);
  });
}

/**
 * Adds a tap operator to the observable which will use the provided notification service to notify the user about events (based on the http status code).
 *
 * @param notificationService - A reference to the notification service
 * @param data - A list of objects which indicate what i18n key is to be used in the notification for a given http status code
 * @param unexpectedStatusData - Notification data for when an unexpected status is encountered.  Omitting this parameter will disable this function.
 * @param logUnexpectedStatus - Log to the console when a status code is encountered that isn't in the data param
 * @param statusCodesToIgnore - A list of any status codes that are to be completely ignored (will not override the data parameter).
 *  <strong>Note: </strong> this is only ignored from the snackbar popup, not the console logging.
 */
export function tapStatusToNotification<T>(
  notificationService: NotificationService,
  data: {statusCode: number; i18nMessage: string; i18nData?: Record<string, unknown>}[],
  unexpectedStatusData?: {i18nMessage: string; i18nData?: Record<string, unknown>},
  logUnexpectedStatus: boolean = true,
  statusCodesToIgnore?: number[]
): MonoTypeOperatorFunction<HttpResponse<T>> {
  return tap((httpResponse: HttpResponse<T>) => {
    if(httpResponse) {
      // Get the status for the status code that we got
      const dataForStatus = data.find(d => httpResponse.status === d.statusCode);
      // If data for this specific code was supplied
      if(dataForStatus) {
        console.debug(`Status code: ${dataForStatus.statusCode}, i18n message: ${dataForStatus.i18nMessage}`);
        if(dataForStatus.i18nMessage) {
          // Display the message via the snackbar
          notificationService.displayI18n(dataForStatus.i18nMessage, dataForStatus.i18nData);
        }
      } else { // Data wasn't provided for this specific status code
        if(logUnexpectedStatus) {
          console.warn(`Unexpected status code ${httpResponse.status}`);
        }
        //Only show the unexpectedStatusData message if the response code is not specified to be ignored
        if(unexpectedStatusData && !statusCodesToIgnore?.find(code => code === httpResponse.status)) {
          console.debug(`Displaying message (${unexpectedStatusData.i18nMessage}) for unexpected status of ${httpResponse.status}`);
          notificationService.displayI18n(unexpectedStatusData.i18nMessage, unexpectedStatusData.i18nData);
        } else {
          console.debug(`Ignoring status ${httpResponse.status}`);
        }
      }
    }
  });
}

/**
 * A simple composition helper that will print the debug message to the console prior to the passed-in observable running
 *
 * @param debugMsg - Message to send to console.debug
 * @param obs$ - Observable to pipe after debug message
 */
export function debugFirst<T>(debugMsg: string, obs$: Observable<T>): Observable<T> {
  return of({}).pipe(tap(() => console.debug(debugMsg)), switchMap(() => obs$));
}

/**
 * A simple composition helper that will take a boolean observable and if it's false, switch over to the provided observable
 *
 * @param obs$ - Observable to check if its false
 * @param ifTrue$ - Observable to switch to if obs$ is false
 */
export function switchMapIfTrue<T>(obs$: Observable<boolean>, ifTrue$: Observable<T>): Observable<boolean | T> {
  return obs$.pipe(switchMap(v => v ? ifTrue$ : of(false)));
}

/**
 * A simple composition helper that will take a boolean observable and if it's false, switch over to the provided observable
 *
 * @param obs$ - Observable to check if its false
 * @param ifFalse$ - Observable to switch to if obs$ is false
 */
export function switchMapIfFalse<T>(obs$: Observable<boolean>, ifFalse$: Observable<T>): Observable<boolean | T> {
  return obs$.pipe(switchMap(v => v ? of(v) : ifFalse$));
}

/**
 * A simple composition helper that will take a list of boolean observables, look at the latest emission from each,
 * and emit true if <strong>any</strong> emitted true. Otherwise false.
 *
 * @param obs$ - List of Observables to check if any are true <strong>at the moment</strong>
 */
export function anyTrue(...obs$: Observable<boolean>[]): Observable<boolean> {
  return combineLatest(obs$).pipe(take(1), map(distinct => distinct.includes(true)));
}

/**
 * A simple composition helper that will take a list of boolean observables, look at the latest emission from each,
 * and emit true if <strong>all</strong> emitted true. Otherwise false.
 *
 * @param obs$ - List of Observables to check if all are true <strong>at the moment</strong>
 */
export function allTrue(...obs$: Observable<boolean>[]): Observable<boolean> {
  return combineLatest(obs$).pipe(take(1), map(distinct => !distinct.includes(false)));
}
